/**
 * AIInsights — Claude-generated Lead Coach card for the buyer Overview page.
 *
 * Data source: GET /api/me/insights
 * Cached server-side for 6h to limit LLM spend; users can manually refresh
 * (force=true) via the refresh icon.
 *
 * Visual:
 *  - Hero summary line at the top
 *  - 3 colour-coded insight cards (good=emerald, warn=amber, tip=blue)
 *  - "Powered by Claude" attribution + last-generated timestamp
 */
import React, { useEffect, useState } from "react";
import { Sparkles, CheckCircle2, AlertTriangle, Lightbulb, RefreshCw, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const KIND_STYLES = {
  good: {
    Icon:  CheckCircle2,
    ring:  "border-emerald-200",
    bg:    "bg-emerald-50/40",
    badge: "bg-emerald-500/15 text-emerald-700",
    label: "Good",
  },
  warn: {
    Icon:  AlertTriangle,
    ring:  "border-amber-200",
    bg:    "bg-amber-50/40",
    badge: "bg-amber-500/15 text-amber-800",
    label: "Action",
  },
  tip: {
    Icon:  Lightbulb,
    ring:  "border-[#0055FF]/25",
    bg:    "bg-[#0055FF]/[0.04]",
    badge: "bg-[#0055FF]/15 text-[#0055FF]",
    label: "Tip",
  },
};

export default function AIInsights() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr]   = useState(null);

  const load = async (force = false) => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.get("/me/insights" + (force ? "?force=true" : ""));
      setData(data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(false); }, []);

  if (busy && !data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center justify-center" data-testid="insights-loading">
        <Loader2 className="size-4 animate-spin text-slate-400" />
        <span className="ml-2 text-sm text-slate-500">Lead Coach is reading your account…</span>
      </div>
    );
  }

  if (err || !data) {
    return null; // fail-quietly — insights are nice-to-have, never block the dashboard
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/60 overflow-hidden" data-testid="ai-insights">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className="inline-grid place-items-center size-9 rounded-lg bg-[#0055FF]/[0.10] text-[#0055FF] flex-shrink-0">
            <Sparkles className="size-4" strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#0055FF] font-semibold">Lead Coach</div>
            <h3 className="text-[15px] font-semibold text-slate-900 leading-snug mt-0.5">
              {data.summary}
            </h3>
          </div>
        </div>
        <button
          onClick={() => load(true)}
          disabled={busy}
          className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors flex-shrink-0"
          aria-label="Regenerate insights"
          title="Regenerate insights"
          data-testid="insights-refresh"
        >
          <RefreshCw className={`size-3.5 ${busy ? "animate-spin" : ""}`} strokeWidth={2} />
        </button>
      </div>

      {/* Insights grid */}
      <div className="px-5 pb-5 grid sm:grid-cols-3 gap-3">
        {(data.insights || []).map((ins, i) => {
          const styl = KIND_STYLES[ins.kind] || KIND_STYLES.tip;
          const Icon = styl.Icon;
          return (
            <div
              key={i}
              className={`rounded-xl border ${styl.ring} ${styl.bg} p-4`}
              data-testid={`insight-${i}-${ins.kind}`}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono font-semibold tracking-[0.14em] uppercase ${styl.badge}`}>
                  <Icon className="size-2.5" strokeWidth={2.4} /> {styl.label}
                </span>
              </div>
              <h4 className="text-[13.5px] font-semibold text-slate-900 tracking-tight leading-snug">{ins.title}</h4>
              <p className="text-[12.5px] text-slate-600 mt-1.5 leading-[1.55]">{ins.body}</p>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-5 py-2.5 border-t border-slate-100 bg-white/60 flex items-center justify-between text-[10.5px] font-mono text-slate-400">
        <span>Generated by Claude Sonnet · refreshes every 6h</span>
        <span>{data.stats?.window_days || 30}-day window</span>
      </div>
    </div>
  );
}
